import {Form, Modal, Row, Col, DatePicker, Input, TimePicker} from "antd";
import React, {useState} from "react";
import {useFormik} from "formik";
import {useTranslation} from "react-i18next";
import moment from "moment";
import {CompanyFilter} from "../../../_defaultComponents/FilterComponents";
import {useStore} from "../../../../hooks/useStore";
import {useFetch} from "../../../../hooks/useFetch";
import {ErrorMessage, SelectField} from "./ShiftScoreStyles";
import ShiftScoreDetailModal from "./ShiftScoreDetailModal";
import localeTr from "antd/es/date-picker/locale/tr_TR";
import localeEn from "antd/es/date-picker/locale/en_US";
import * as Yup from "yup";

const ShiftScoreModal = ({visible, onCancel, setIsModalOpen}) => {
    const {t, i18n} = useTranslation();
    const stores = useStore();
    const [searchText, setSearchText] = useState("");
    const [courierId, setCourierId] = useState("");

    const [, data, ,] = useFetch({
        fetchFn: stores.courierStore.getCourierList.bind(null, {
            limit: 10,
            skip: 0,
            statuses: [],
            types: [],
            searchText
        }),
        dependencies: [searchText],
        condition: !stores.meStore.checkRole("isCompanyUser"),
        initialCall: true
    });

    const [, subCompanies, ,] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.companyStore.getSubCompanyList.bind(null, {
            companyId: stores.meStore.map.filters.company,
            limit: 1000
        }),
        condition:
            (["", "pool"].indexOf(stores.meStore.map.filters.company) === -1 ||
                stores.meStore.checkRole("isCompanyUser")) &&
            !!stores.meStore.map.filters.company,
        dependencies: [stores.meStore.map.filters.company, stores.meStore.map.filters.city],
        initialCall:
            ["", "pool"].indexOf(stores.meStore.map.filters.company) === -1 ||
            stores.meStore.checkRole("isCompanyUser")
    });

    const [, courierData, ,] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.courierStore.getCourier.bind(null, {
            courierId: courierId
        }),
        dependencies: [courierId],
        condition: !stores.meStore.checkRole("isCompanyUser") && courierId
    });

    const formValidation = Yup.object().shape({
        date: Yup.string().required(t("validation.required", {ns: "common"})),
        courier: Yup.string().required(t("validation.required", {ns: "common"})),
        company: Yup.string().required(t("validation.required", {ns: "common"})),
        subCompany: Yup.string().required(t("validation.required", {ns: "common"})),
        shiftStartTime: Yup.string().required(t("validation.required", {ns: "common"})),
        shiftEndTime: Yup.string().required(t("validation.required", {ns: "common"})),
        workingTime: Yup.number().required(t("validation.required", {ns: "common"})),
        packageCount: Yup.number()
            .required(t("validation.required", {ns: "common"}))
            .max(99)
    });

    const formik = useFormik({
        initialValues: {
            date: "",
            courier: "",
            company: "",
            subCompany: "",
            subCompanyId: "",
            shiftStartTime: "",
            shiftEndTime: "",
            workingTime: "",
            packageCount: ""
        },
        validationSchema: formValidation,
        enableReinitialize: true,
        onSubmit: async () => {
            const report = {
                shiftDate: moment(formik.values.date).format("YYYY-MM-DD"),
                companyId: stores.appStore.companyListFilter
                    ?.filter(c => c._id === values.company)
                    ?.map(c => c._id)[0],
                subCompanyId: values.subCompanyId,
                courierId: courierId,
                shiftStartTime:
                    values.shiftStartTime &&
                    moment(values.shiftStartTime).format("HH") +
                        ":" +
                        moment(values.shiftStartTime).format("mm"),
                shiftEndTime:
                    values.shiftEndTime &&
                    moment(values.shiftEndTime).format("HH") +
                        ":" +
                        moment(values.shiftEndTime).format("mm"),
                workTimeMinute: Number(values.workingTime),
                packageCount: Number(values.packageCount)
            };
            await stores.shiftStore.createShiftReport(report);
            setIsModalOpen(false);
        }
    });

    const {values, setFieldValue, handleSubmit, errors, touched} = formik;

    const getDisabledDate = current => {
        let customDate = moment().format("DD.MM.YYYY");
        return current && current > moment(customDate, "DD.MM.YYYY").add(1, "days");
    };

    return (
        <Modal
            centered
            width={920}
            visible={visible}
            onOk={handleSubmit}
            onCancel={onCancel}
            okText={t("modal.saveButton", {ns: "common"})}
            cancelText={t("modal.cancelButton2", {ns: "common"})}
        >
            <Form>
                <Row>
                    <Col span={11}>
                        <Form.Item>
                            <h4>{t("fields.date", {ns: "common"})}</h4>
                            <DatePicker
                                value={values.date}
                                format={"DD.MM.YYYY"}
                                onSelect={date => setFieldValue("date", date)}
                                disabledDate={getDisabledDate}
                                locale={localeTr}
                            />
                            {errors.date && touched.date ? (
                                <ErrorMessage>{errors.date}</ErrorMessage>
                            ) : null}
                        </Form.Item>
                        <Form.Item>
                            <h4>{t("fields.courier", {ns: "common"})}</h4>
                            <SelectField
                                allowClear
                                showSearch
                                placeholder={t("filters.addFilter", {ns: "common"})}
                                value={values.courier}
                                onSearch={value => {
                                    setSearchText(value);
                                }}
                                onChange={(value, key) => {
                                    setFieldValue("courier", value);
                                    setCourierId(key?.key);
                                }}
                            >
                                {data.rows &&
                                    data?.rows.map(courier => (
                                        <SelectField.Option
                                            key={courier._id}
                                            value={courier.fullname}
                                            data-testid="courierSelect_option"
                                        >
                                            {courier.fullname}
                                        </SelectField.Option>
                                    ))}
                            </SelectField>
                            {errors.courier && touched.courier ? (
                                <ErrorMessage>{errors.courier}</ErrorMessage>
                            ) : null}
                        </Form.Item>
                        <Form.Item>
                            <h4>{t("fields.company", {ns: "common"})}</h4>
                            <CompanyFilter
                                hideTitle
                                showPool={false}
                                value={values.company}
                                onChange={value => {
                                    setFieldValue("company", value);
                                    setFieldValue("subCompany", "");
                                    stores.meStore.map.filters.company = value;
                                }}
                            />
                            {errors.company && touched.company ? (
                                <ErrorMessage>{errors.company}</ErrorMessage>
                            ) : null}
                        </Form.Item>
                        <Form.Item>
                            <h4>{t("fields.subcompany", {ns: "common"})}</h4>
                            <SelectField
                                allowClear
                                showSearch
                                placeholder={t("filters.addFilter", {ns: "common"})}
                                value={values.subCompany}
                                onChange={(value, key) => {
                                    setFieldValue("subCompany", value);
                                    setFieldValue("subCompanyId", key.key);

                                    stores.meStore.map.filters.subCompany = value;
                                }}
                            >
                                {subCompanies.rows &&
                                    subCompanies?.rows.map(subcompany => (
                                        <SelectField.Option
                                            key={subcompany._id}
                                            value={subcompany.name}
                                            data-testid="subcompanySelect_option"
                                        >
                                            {subcompany.name}
                                        </SelectField.Option>
                                    ))}
                            </SelectField>
                            {errors.subCompany && touched.subCompany ? (
                                <ErrorMessage>{errors.subCompany}</ErrorMessage>
                            ) : null}
                        </Form.Item>
                        <Form.Item>
                            <h4>{t("shiftScore.shiftStartTime", {ns: "shiftScore"})}</h4>
                            <TimePicker
                                format={"HH:mm"}
                                allowClear={false}
                                minuteStep={15}
                                onSelect={date => setFieldValue("shiftStartTime", date)}
                                locale={i18n?.resolvedLanguage === "en" ? localeEn : localeTr}
                            />

                            {errors.shiftStartTime && touched.shiftStartTime ? (
                                <ErrorMessage>{errors.shiftStartTime}</ErrorMessage>
                            ) : null}
                        </Form.Item>
                        <Form.Item>
                            <h4>{t("shiftScore.shiftEndTime", {ns: "shiftScore"})}</h4>
                            <TimePicker
                                format={"HH:mm"}
                                allowClear={false}
                                minuteStep={15}
                                onSelect={date => setFieldValue("shiftEndTime", date)}
                                locale={i18n?.resolvedLanguage === "en" ? localeEn : localeTr}
                            />

                            {errors.shiftEndTime && touched.shiftEndTime ? (
                                <ErrorMessage>{errors.shiftEndTime}</ErrorMessage>
                            ) : null}
                        </Form.Item>
                        <Form.Item>
                            <h4>{t("shiftScore.workTime", {ns: "shiftScore"})}</h4>
                            <Input
                                type="number"
                                placeholder={t("shiftScore.workTime", {ns: "shiftScore"}) + " (dk)"}
                                onChange={e => setFieldValue("workingTime", e.target.value)}
                                value={values.workingTime}
                            />

                            {errors.workingTime && touched.workingTime ? (
                                <ErrorMessage>{errors.workingTime}</ErrorMessage>
                            ) : null}
                        </Form.Item>
                        <Form.Item>
                            <h4>{t("shiftScore.packageCount", {ns: "shiftScore"})}</h4>
                            <Input
                                type="number"
                                placeholder={t("shiftScore.packageCount", {ns: "shiftScore"})}
                                onChange={e =>
                                    e.target.value <= 99
                                        ? setFieldValue("packageCount", e.target.value)
                                        : 99
                                }
                                value={values.packageCount}
                            />

                            {errors.packageCount && touched.packageCount ? (
                                <ErrorMessage>{errors.packageCount}</ErrorMessage>
                            ) : null}
                        </Form.Item>
                    </Col>
                    <Col offset={1} span={12}>
                        <ShiftScoreDetailModal
                            reportDate={values.date && moment(values.date).format("DD.MM.YYYY")}
                            fullname={courierData?.data?.fullname}
                            tckn={courierData?.data?.tckn}
                            cellPhone={courierData?.data?.cellPhone}
                            companyName={stores.appStore.companyListFilter
                                ?.filter(c => c._id === values.company)
                                ?.map(c => c.companyName)}
                            subCompanyName={values.subCompany}
                            shiftStartTime={
                                values.shiftStartTime &&
                                moment(values.shiftStartTime).format("HH") +
                                    ":" +
                                    moment(values.shiftStartTime).format("mm")
                            }
                            shiftEndTime={
                                values.shiftEndTime &&
                                moment(values.shiftEndTime).format("HH") +
                                    ":" +
                                    moment(values.shiftEndTime).format("mm")
                            }
                            workTimeMinute={values.workingTime}
                            busyTimeMinute={
                                values.workingTime &&
                                moment(new Date(values.shiftEndTime)).diff(
                                    moment(new Date(values.shiftStartTime)),
                                    "minutes"
                                ) - values.workingTime
                            }
                            packageCount={values.packageCount}
                            verifiedPackageCount={values.verifiedPackageCount}
                        />
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default ShiftScoreModal;
